import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import type { CashierProfile } from '../hooks/useCashierAuth';
import type { DenominationItem } from '../utils/denominations';
import { describeThisDevice } from '../utils/deviceInfo';

/**
 * Error payload. `data` (e.g. WRONG_DEVICE `{ shiftDevice }` / `{ shiftDeviceId }`) and `currentStatus`
 * (INVALID_SHIFT_STATE) are kept so pages can act on them. `data` is typed `any` so this payload stays
 * assignable to every success shape; always check `success` before reading `data`.
 */
type ApiErrorPayload = { success: false; message?: string; code?: string; data?: any; currentStatus?: string };

function asApiErrorPayload(e: unknown): ApiErrorPayload {
  if (axios.isAxiosError(e)) {
    const payload = e.response?.data as any;
    if (payload && typeof payload === 'object') {
      return {
        success: false,
        message: typeof payload.message === 'string' ? payload.message : e.message,
        code: typeof payload.code === 'string' ? payload.code : undefined,
        data: payload.data && typeof payload.data === 'object' ? payload.data : undefined,
        currentStatus: typeof payload.currentStatus === 'string' ? payload.currentStatus : undefined,
      };
    }
    return { success: false, message: e.response?.status ? `Request failed (${e.response.status})` : e.message };
  }
  return { success: false, message: 'Request failed' };
}

function createCashierClient(): AxiosInstance {
  const c = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  c.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  c.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('cashier-auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return c;
}

const cashierClient = createCashierClient();

export type CashierShift = {
  id: string;
  status: string;
  shiftDate?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  actualStartAt?: string;
  actualEndAt?: string;
  openedAt?: string;
  closedAt?: string;
  openingCashBalance?: number;
  closingCashCounted?: number;
  expectedClosingCash?: number;
  discrepancyAmount?: number;
  discrepancyFlagged?: boolean;
  deviceId?: string | null;
  /** Name of the pinned (scheduled) or bound (active) device; null when chosen at start. */
  deviceName?: string | null;
  checkedInAt?: string | null;
};

export type DeviceStatus =
  | 'pending_approval'
  | 'active'
  | 'temporarily_blocked'
  | 'compromised'
  | 'lost'
  | 'retired';

/** Server view of a cashier device. `shortCode` = last 4 hex chars of the id, for matching on screen. */
export type DeviceView = {
  id: string;
  name: string | null;
  status: DeviceStatus;
  shortCode: string;
  approvedAt: string | null;
};

export type DeviceRef = { id: string; name: string | null };

export type StartBlocker =
  | 'DEVICE_UNKNOWN'
  | 'DEVICE_NOT_APPROVED'
  | 'DEVICE_BLOCKED'
  | 'DEVICE_MISMATCH'
  | 'DEVICE_BUSY';

/** POST /cashier/shifts/:id/check-in */
export type CheckInResult = {
  shift: { id: string; status: string; checkedInAt: string | null };
  device: DeviceView | null;
  ready: boolean;
  blocker: StartBlocker | null;
  message: string | null;
  pinnedDevice: DeviceRef | null;
};

export type CashierMe = {
  cashier: CashierProfile;
  shop: { id: string; name?: string; status?: string } | null;
  currentShift: {
    id: string;
    status?: string;
    deviceId?: string | null;
    openedAt?: string | null;
    openingCashBalance?: number | null;
    closedAt?: string | null;
  } | null;
  device: DeviceView | null;
};

export type CashierTransactionStatus = 'completed' | 'pending_approval' | 'rejected' | 'cancelled';

export type CashierTransaction = {
  id: string;
  type?: string;
  amount?: number;
  cashDelta?: number;
  metadata?: { cashPaid?: number; cash_paid?: number; [key: string]: unknown };
  playerId?: string;
  createdAt?: string;
  /** @deprecated use transactionStatus */
  status?: string;
  transactionStatus?: CashierTransactionStatus;
};

export type CashierRedeemResult = {
  cashierTransaction: CashierTransaction;
  pendingApproval?: boolean;
  approvalId?: string;
  walletTransfer?: unknown;
  shiftId?: string;
};

export type CashierCashDrop = {
  id: string;
  shiftId: string;
  totalAmount?: number;
  status?: string;
  createdAt?: string;
};

/**
 * Sign in. The server recognises this device from the fingerprint (or registers it as pending) and
 * binds it to the session; `data.device` is that device, or null. Top-level `deviceFingerprint` is kept
 * for older backends.
 */
export async function cashierLogin(payload: {
  identifier: string;
  password: string;
  deviceFingerprint: string;
}): Promise<{
  success: boolean;
  data?: { accessToken: string; cashier: CashierProfile; shop: any; device?: DeviceView | null };
  message?: string;
}> {
  const info = describeThisDevice();
  const { data } = await cashierClient.post('/cashier-auth/login', {
    username: payload.identifier,
    identifier: payload.identifier,
    password: payload.password,
    device: {
      fingerprint: payload.deviceFingerprint,
      name: info.name,
      type: info.type,
      os: info.os || undefined,
      browser: info.browser || undefined,
    },
    deviceFingerprint: payload.deviceFingerprint,
  });
  return data;
}

export type ForgotPasswordRequestResponse = {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    channel: 'email' | 'phone';
    expiresInSeconds: number;
    resend_count?: number;
    next_resend_at?: string;
    expires_at?: string;
  };
};

export async function requestForgotPasswordOtp(contact: string): Promise<ForgotPasswordRequestResponse> {
  const { data } = await cashierClient.post('/cashier-auth/forgot-password/request-otp', { contact });
  return data;
}

export async function verifyForgotPasswordOtp(
  token: string,
  otp: string
): Promise<{ success: boolean; message?: string; data?: { token: string } }> {
  const { data } = await cashierClient.post('/cashier-auth/forgot-password/verify-otp', { token, otp });
  return data;
}

export async function confirmForgotPasswordReset(payload: {
  token: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await cashierClient.post('/cashier-auth/forgot-password/confirm', payload);
  return data;
}

export async function fetchCashierMe(): Promise<{ success: boolean; data?: CashierMe; message?: string; code?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/me');
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierListAssignedShifts(): Promise<{ success: boolean; data?: CashierShift[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/shifts/assigned');
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

/** GET /cashier/shifts/:id — `device` is the device the shift runs on (or is pinned to), if any. */
export type ShiftDetail = {
  shift: CashierShift;
  transactions?: CashierTransaction[];
  cashDrops?: CashierCashDrop[];
  device?: DeviceRef | null;
  reconciliation?: unknown;
};

export async function cashierGetShift(
  shiftId: string
): Promise<{ success: boolean; data?: ShiftDetail; message?: string; code?: string }> {
  try {
    const { data } = await cashierClient.get(`/cashier/shifts/${encodeURIComponent(shiftId)}`);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

/** Record the check-in (idempotent) and get the start readiness for this device. */
export async function cashierCheckInShift(
  shiftId: string
): Promise<{ success: boolean; data?: CheckInResult; code?: string; message?: string; currentStatus?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(shiftId)}/check-in`, {});
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

/** Approve this (pending) device with a one-time pairing code from the manager, e.g. "K7QM-3XPD". */
export async function cashierPairDevice(
  code: string
): Promise<{ success: boolean; data?: { device: DeviceView; alreadyApproved: boolean }; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/device/pair', { code });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

/** Start the shift on the session device (the server ignores any device id in the body). */
export async function cashierStartShift(
  shiftId: string,
  payload: { denominations: DenominationItem[] }
): Promise<{ success: boolean; data?: CashierShift; code?: string; message?: string; currentStatus?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(shiftId)}/start`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierCloseShiftBlind(
  shiftId: string,
  payload: { denominations: DenominationItem[] }
): Promise<{
  success: boolean;
  data?: {
    shiftId: string;
    actualClosingCash: number;
    expectedClosingCash: number;
    discrepancyAmount: number;
    discrepancyFlagged: boolean;
    status: string;
  };
  code?: string;
  message?: string;
}> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(shiftId)}/close`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierCreateCashDrop(payload: {
  shiftId: string;
  denominations: DenominationItem[];
}): Promise<{ success: boolean; data?: CashierCashDrop; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/cash-drops', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierListCashDrops(
  shiftId: string
): Promise<{ success: boolean; data?: CashierCashDrop[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/cash-drops', { params: { shiftId } });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierListTransactions(
  shiftId: string
): Promise<{ success: boolean; data?: CashierTransaction[]; message?: string }> {
  try {
    const { data } = await cashierClient.get('/cashier/transactions', { params: { shiftId } });
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierInitiateHandover(
  fromShiftId: string,
  payload: { toShiftId: string; denominations: DenominationItem[] }
): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(fromShiftId)}/handover`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierConfirmHandover(
  toShiftId: string,
  payload: { denominations: DenominationItem[] }
): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/shifts/${encodeURIComponent(toShiftId)}/handover/confirm`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierFetchPlayers(params?: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ success: boolean; data?: any[]; pagination?: any }> {
  try {
    const { data } = await cashierClient.get('/cashier/players', { params });
    return data;
  } catch {
    return { success: false, data: [], pagination: undefined };
  }
}

export async function cashierCreatePlayer(payload: {
  phone?: string;
  email?: string;
  username?: string;
  password?: string;
}): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post('/cashier/players', payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierDepositCredits(
  playerId: string,
  payload: { amount: number }
): Promise<{ success: boolean; data?: any; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/deposit`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}

export async function cashierRedeemCredits(
  playerId: string,
  payload: { amount: number; cash_paid: number }
): Promise<{ success: boolean; data?: CashierRedeemResult; code?: string; message?: string }> {
  try {
    const { data } = await cashierClient.post(`/cashier/players/${encodeURIComponent(playerId)}/redeem`, payload);
    return data;
  } catch (e: unknown) {
    return asApiErrorPayload(e);
  }
}
